import { useState, useEffect, useRef } from "react";
import { linkifyLegalRefs, detectRefs } from "../utils/legalRefs";

interface UseLegalRefsOptions {
  /** Only process when streaming is complete */
  waitForComplete?: boolean;
  /** Debounce delay in ms (for streaming content) */
  debounceMs?: number;
  /** Enable/disable processing */
  enabled?: boolean;
}

interface UseLegalRefsResult {
  /** Processed content with links */
  processedContent: string;
  /** Whether processing is in progress */
  isProcessing: boolean;
  /** Number of references found */
  refCount: number;
}

/**
 * Hook to process text content and linkify legal references
 *
 * @param content - The raw content to process
 * @param isComplete - Whether the content is complete (for streaming scenarios)
 * @param options - Processing options
 */
export function useLegalRefs(
  content: string,
  isComplete: boolean,
  options: UseLegalRefsOptions = {},
): UseLegalRefsResult {
  const { waitForComplete = true, debounceMs = 300, enabled = true } = options;

  const [processedContent, setProcessedContent] = useState(content);
  const [isProcessing, setIsProcessing] = useState(false);
  const [refCount, setRefCount] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const processedContentRef = useRef<string>("");
  const lastProcessedRawRef = useRef<string>("");

  useEffect(() => {
    if (!enabled) {
      setProcessedContent(content);
      return;
    }

    // If waiting for complete and still streaming, just pass through
    if (waitForComplete && !isComplete) {
      setProcessedContent(content);
      return;
    }

    // If content hasn't changed from last processed, restore cached result
    if (content === lastProcessedRawRef.current) {
      // Restore cached processed content (state may have been reset to "" when disabled)
      if (processedContentRef.current && processedContentRef.current !== processedContent) {
        setProcessedContent(processedContentRef.current);
      }
      return;
    }

    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const process = async () => {
      // Quick check if there are any potential refs
      const potentialRefs = detectRefs(content);

      if (potentialRefs.length === 0) {
        setProcessedContent(content);
        setRefCount(0);
        processedContentRef.current = content; // Cache for restore on re-open
        lastProcessedRawRef.current = content;
        return;
      }

      setIsProcessing(true);
      setRefCount(potentialRefs.length);

      try {
        const result = await linkifyLegalRefs(content);
        setProcessedContent(result);
        processedContentRef.current = result;
        lastProcessedRawRef.current = content;
      } catch (error) {
        console.error("Error processing legal refs:", error);
        setProcessedContent(content);
      } finally {
        setIsProcessing(false);
      }
    };

    // Debounce the processing
    if (debounceMs > 0 && !isComplete) {
      debounceRef.current = setTimeout(process, debounceMs);
    } else {
      process();
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [content, isComplete, waitForComplete, debounceMs, enabled]);

  return {
    processedContent,
    isProcessing,
    refCount,
  };
}
